/**
 * Normalises the wire contract into the flat model the renderer consumes.
 *
 * WHY AN ADAPTER RATHER THAN A REWRITE. The contract and the renderer disagree
 * on three things, and the disagreement is not accidental -- spec 0.1 moved
 * `kind` into `attributes`, replaced `parentId` with a `partOf` relation, and
 * collapsed `spans[]` to a single `range` (ADR-0016). Resolving that here, at
 * the boundary, leaves annotation-mdast-plugin.ts untouched. That matters: the
 * plugin is the only thing in the estate that attaches highlights to the parsed
 * tree rather than splicing the source string, and its test suite asserts on
 * rendered HTML precisely because its predecessor asserted on the spliced
 * string and passed while block structure silently broke. Re-shaping the
 * plugin to a new annotation type would put that regression net at risk for no
 * behavioural gain.
 *
 * The model below is therefore an INTERNAL shape, not a second contract. It
 * keeps `spans` as an array because the plugin reads every span, and keeps
 * `kind`/`parentId` flat because the plugin and the slicer both branch on them.
 */

import {
  type Annotation,
  type AnnotationLayer,
  ATTRIBUTE_KEY_KIND,
  RELATION_TYPE_PART_OF,
  type Span
} from './cmx-document-content-types';

/** Internal, renderer-facing annotation. Not a wire type. */
export type ViewAnnotation = {
  id: string;
  kind: string;
  parentId: string | null;
  spans: Span[];
  attributes: { key: string; value: string }[];
};

/** Reads a named attribute off a wire annotation. */
export function attributeValue(
  annotation: Annotation,
  key: string
): string | null {
  return annotation.attributes?.find((a) => a.key === key)?.value ?? null;
}

/**
 * One wire annotation -> one internal annotation.
 *
 * A missing `kind` attribute yields `''` rather than throwing: an unknown kind
 * is renderable (it simply matches no section filter), whereas a throw would
 * make one malformed annotation cost the whole document.
 */
export function toViewAnnotation(annotation: Annotation): ViewAnnotation {
  const parentOf = annotation.relations?.find(
    (relation) => relation.type === RELATION_TYPE_PART_OF
  );

  return {
    id: annotation.id,
    kind: attributeValue(annotation, ATTRIBUTE_KEY_KIND) ?? '',
    parentId: parentOf?.targetId ?? null,
    // The contract carries exactly one range; the renderer reads a list.
    spans: annotation.range ? [annotation.range] : [],
    attributes: annotation.attributes ?? []
  };
}

/**
 * A whole layer's annotations. `null` layer (every layer 1-3 read) yields an
 * empty list, so callers need no special case for an unannotated body.
 */
export function toViewAnnotations(
  layer: AnnotationLayer | null | undefined
): ViewAnnotation[] {
  if (!layer?.annotations) {
    return [];
  }
  return layer.annotations.map(toViewAnnotation);
}
